import { GroupBase, chakraComponents } from "chakra-react-select";
import FancyBearImage from "components/FancyBearImage";
import {
  AsyncSmartSelect,
  AsyncSmartSelectProps,
  defaultOptionStyles,
} from "components/SmartSelect";
import { fancyBearsTokensAmount } from "config";

type TokenSelectOption = {
  value: number;
  label: string;
};

type TokenSelectProps = Omit<
  AsyncSmartSelectProps<TokenSelectOption, false, GroupBase<TokenSelectOption>>,
  | "onChange"
  | "value"
  | "defaultOptions"
  | "loadOptions"
  | "components"
  | "blurInputOnSelect"
> & {
  tokenId: number;
  defaultTokens: number[];
  firstTokenId?: number;
  lastTokenId?: number;
  tokenPrefix?: string;
  onChange?: (tokenId?: number) => void;
};

const generateOptions = (
  inputValue: string,
  firstTokenId: number,
  lastTokenId: number,
  tokenPrefix: string
): TokenSelectOption[] => {
  const options = [];
  const valueAsNumber = parseInt(inputValue, 10);

  if (Number.isInteger(valueAsNumber)) {
    const generateOptionsFrom = Math.max(
      Math.min(valueAsNumber, lastTokenId),
      firstTokenId
    );

    let counter = 0;
    let tokenId = generateOptionsFrom;
    while (tokenId < lastTokenId && counter <= 5) {
      options.push({
        value: tokenId,
        label: `${tokenPrefix}${tokenId}`,
      });
      tokenId = parseInt(`${generateOptionsFrom}${counter}`);
      counter++;
    }
  }

  return options;
};

const TokenSelect = ({
  tokenId,
  defaultTokens,
  firstTokenId = 1,
  lastTokenId = fancyBearsTokensAmount,
  tokenPrefix = "#",
  chakraStyles = {},
  onChange,
  ...rest
}: TokenSelectProps) => {
  const defaultOptions = defaultTokens.length
    ? defaultTokens
        .map((tokenId) => ({
          label: `${tokenPrefix}${tokenId}`,
          value: tokenId,
        }))
        .sort((a, b) => a.value - b.value)
    : generateOptions(
        tokenId.toString(),
        firstTokenId,
        lastTokenId,
        tokenPrefix
      ).slice(1);

  return (
    <AsyncSmartSelect<TokenSelectOption, false, GroupBase<TokenSelectOption>>
      value={{ value: tokenId, label: `#${tokenId}` }}
      components={{
        Option: ({ children, ...props }) => (
          <chakraComponents.Option {...props}>
            <FancyBearImage
              tokenId={props.data.value}
              size="256"
              w="8"
              h="8"
              flex="none"
              mr="3"
            />

            {children}
          </chakraComponents.Option>
        ),
        Input: ({ children, type, ...props }) => (
          <chakraComponents.Input
            type="number"
            min={firstTokenId}
            max={lastTokenId}
            {...props}
          />
        ),
      }}
      chakraStyles={{
        control: (provided) => ({
          ...provided,
        }),
        option: (provided) => ({
          ...provided,
          ...defaultOptionStyles,
          p: "2",
        }),
        ...chakraStyles,
      }}
      defaultOptions={defaultOptions}
      loadOptions={(inputValue: string) =>
        Promise.resolve(
          generateOptions(inputValue, firstTokenId, lastTokenId, tokenPrefix)
        )
      }
      onChange={(option) => onChange && onChange(option?.value)}
      blurInputOnSelect
      {...rest}
    />
  );
};

export default TokenSelect;
